/**
 * Status vocabularies shared by every app. Raw provider values are always retained next to the
 * normalized value (spec section 6); nothing here may turn an unknown into a pass.
 */
import { z } from 'zod';

/** Normalized record status (spec section 6). */
export const STANDARD_STATUSES = [
  'PASS',
  'FAIL',
  'NEEDS_ATTENTION',
  'DUE_SOON',
  'UNKNOWN',
  'NOT_APPLICABLE',
  'DEACTIVATED',
  'UNSUPPORTED',
] as const;
export const StandardStatusSchema = z.enum(STANDARD_STATUSES);
export type StandardStatus = z.infer<typeof StandardStatusSchema>;

/** Display labels (spec spells NEEDS ATTENTION / DUE SOON / NOT APPLICABLE with spaces). */
export const STANDARD_STATUS_LABEL: Record<StandardStatus, string> = {
  PASS: 'PASS',
  FAIL: 'FAIL',
  NEEDS_ATTENTION: 'NEEDS ATTENTION',
  DUE_SOON: 'DUE SOON',
  UNKNOWN: 'UNKNOWN',
  NOT_APPLICABLE: 'NOT APPLICABLE',
  DEACTIVATED: 'DEACTIVATED',
  UNSUPPORTED: 'UNSUPPORTED',
};

/** Statuses that count as "failing" for ask inclusion and counts. */
export const FAILING_STATUSES: readonly StandardStatus[] = ['FAIL', 'NEEDS_ATTENTION', 'DUE_SOON'];
/** Statuses that count as "ok" (hidden unless Show OK). */
export const OK_STATUSES: readonly StandardStatus[] = ['PASS', 'NOT_APPLICABLE'];

export function isFailingStatus(status: StandardStatus): boolean {
  return FAILING_STATUSES.includes(status);
}
export function isOkStatus(status: StandardStatus): boolean {
  return OK_STATUSES.includes(status);
}

export const SEVERITY_LEVELS = ['critical', 'high', 'medium', 'low', 'info'] as const;
export const SeverityLevelSchema = z.enum(SEVERITY_LEVELS);
export type SeverityLevel = z.infer<typeof SeverityLevelSchema>;
/** Lower rank = more severe; used for deterministic sorting (A12). */
export const SEVERITY_RANK: Record<SeverityLevel, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
export const SEVERITY_CHIP: Record<SeverityLevel, string> = {
  critical: 'CRIT',
  high: 'HIGH',
  medium: 'MED',
  low: 'LOW',
  info: 'INFO',
};

export const EMPLOYMENT_STATES = [
  'current',
  'contractor',
  'future_hire',
  'former',
  'out_of_scope',
  'service_account',
] as const;
export const EmploymentStateSchema = z.enum(EMPLOYMENT_STATES);
export type EmploymentState = z.infer<typeof EmploymentStateSchema>;
/** Employment states included in the default Employee Compliance report (spec section 8). */
export const REPORTED_EMPLOYMENT_STATES: readonly EmploymentState[] = ['current', 'contractor', 'future_hire'];

export const EMPLOYEE_CHECK_STATUSES = [
  'PASS',
  'FAIL',
  'DUE_SOON',
  'MISCONFIGURED',
  'EXCLUDED',
  'UNKNOWN',
  'UNSUPPORTED',
] as const;
export const EmployeeCheckStatusSchema = z.enum(EMPLOYEE_CHECK_STATUSES);
export type EmployeeCheckStatus = z.infer<typeof EmployeeCheckStatusSchema>;

/** Employee Compliance report columns (spec section 8), in order. */
export const EMPLOYEE_CHECKS = [
  'policies',
  'identity_mfa',
  'background_check',
  'agent_installed',
  'password_manager',
  'disk_encryption',
  'antivirus',
  'auto_updates',
  'lock_screen',
  'security_training',
  'hipaa_training',
  'ai_training',
] as const;
export const EmployeeCheckSchema = z.enum(EMPLOYEE_CHECKS);
export type EmployeeCheck = z.infer<typeof EmployeeCheckSchema>;
export const EMPLOYEE_CHECK_LABEL: Record<EmployeeCheck, string> = {
  policies: 'Policies',
  identity_mfa: 'Identity MFA',
  background_check: 'Background Check',
  agent_installed: 'Agent Installed',
  password_manager: 'Password Manager',
  disk_encryption: 'Disk Encryption',
  antivirus: 'Antivirus',
  auto_updates: 'Auto Updates',
  lock_screen: 'Lock Screen',
  security_training: 'Security Training',
  hipaa_training: 'HIPAA Training',
  ai_training: 'AI Training',
};

/** Boolean the source may omit. Never coerce 'unknown' to false. */
export const TriSchema = z.union([z.boolean(), z.literal('unknown')]);
export type Tri = z.infer<typeof TriSchema>;

export function triFromNullable(value: boolean | null | undefined): Tri {
  return typeof value === 'boolean' ? value : 'unknown';
}

/** Dataset / snapshot completeness. */
export const COMPLETENESS_STATES = ['complete', 'partial', 'stale', 'incomplete', 'unsynced'] as const;
export const CompletenessStateSchema = z.enum(COMPLETENESS_STATES);
export type CompletenessState = z.infer<typeof CompletenessStateSchema>;

/** The ten UI component states (spec section 22). */
export const UI_STATES = [
  'empty',
  'loading',
  'complete',
  'stale',
  'partial',
  'rate_limited',
  'disconnected',
  'unauthorized',
  'unsupported',
  'failed',
] as const;
export const UiStateSchema = z.enum(UI_STATES);
export type UiState = z.infer<typeof UiStateSchema>;

/** Sync job states, exactly as spec section 6. */
export const SYNC_JOB_STATES = [
  'queued',
  'preflight',
  'fetching',
  'validating',
  'committing',
  'succeeded',
  'waiting_rate_limit',
  'paused',
  'interrupted',
  'partial',
  'failed',
  'cancelled',
] as const;
export const SyncJobStateSchema = z.enum(SYNC_JOB_STATES);
export type SyncJobState = z.infer<typeof SyncJobStateSchema>;
export const ACTIVE_SYNC_JOB_STATES: readonly SyncJobState[] = [
  'queued',
  'preflight',
  'fetching',
  'validating',
  'committing',
  'waiting_rate_limit',
];
export const TERMINAL_SYNC_JOB_STATES: readonly SyncJobState[] = ['succeeded', 'partial', 'failed', 'cancelled'];
export const RESUMABLE_SYNC_JOB_STATES: readonly SyncJobState[] = ['paused', 'interrupted', 'partial', 'failed'];

export function isActiveSyncState(state: SyncJobState): boolean {
  return ACTIVE_SYNC_JOB_STATES.includes(state);
}

/** Dataset run states (per dataset inside a run). */
export const DATASET_RUN_STATES = [
  'pending',
  'fetching',
  'waiting_rate_limit',
  'validating',
  'succeeded',
  'failed',
  'skipped_unsupported',
  'skipped_permission',
  'cancelled',
] as const;
export const DatasetRunStateSchema = z.enum(DATASET_RUN_STATES);
export type DatasetRunState = z.infer<typeof DatasetRunStateSchema>;

/** Connection health as reported to the server and shown in the rail. */
export const CONNECTION_HEALTH = ['healthy', 'degraded', 'unauthorized', 'disconnected', 'unknown'] as const;
export const ConnectionHealthSchema = z.enum(CONNECTION_HEALTH);
export type ConnectionHealth = z.infer<typeof ConnectionHealthSchema>;

/** Entitlement lifecycle shown to the renderer. */
export const ENTITLEMENT_STATES = ['active', 'renewing', 'expiring', 'expired', 'denied'] as const;
export const EntitlementStateSchema = z.enum(ENTITLEMENT_STATES);
export type EntitlementState = z.infer<typeof EntitlementStateSchema>;

/** Stable error codes surfaced in sync issues and IPC errors. */
export const SYNC_ERROR_CODES = [
  'network',
  'timeout',
  'rate_limited',
  'unauthorized',
  'token_conflict',
  'forbidden',
  'not_found',
  'malformed_page',
  'duplicate_cursor',
  'duplicate_id',
  'missing_continuation',
  'unexpected_empty_page',
  // added by desktop-worker fix pass (SYNC-08): provider total differs from the fetched count.
  'total_mismatch',
  'server_error',
  'unsupported_dataset',
  'permission_missing',
  'workspace_ambiguous',
  'tenant_mismatch',
  'terms_not_accepted',
  'disk_full',
  'lease_unavailable',
  'entitlement_expired',
  'cancelled',
  'internal',
] as const;
export const SyncErrorCodeSchema = z.enum(SYNC_ERROR_CODES);
export type SyncErrorCode = z.infer<typeof SyncErrorCodeSchema>;

export const REMEDY_ACTIONS = ['reconnect', 'grant_permission', 'wait', 'resume', 'contact_admin', 'none'] as const;
export const RemedyActionSchema = z.enum(REMEDY_ACTIONS);
export type RemedyAction = z.infer<typeof RemedyActionSchema>;

/** Default remedy per error code; adapters may refine. */
export const DEFAULT_REMEDY: Record<SyncErrorCode, RemedyAction> = {
  network: 'resume',
  timeout: 'resume',
  rate_limited: 'wait',
  unauthorized: 'reconnect',
  token_conflict: 'reconnect',
  forbidden: 'grant_permission',
  not_found: 'none',
  malformed_page: 'contact_admin',
  duplicate_cursor: 'resume',
  duplicate_id: 'resume',
  missing_continuation: 'resume',
  unexpected_empty_page: 'resume',
  total_mismatch: 'none',
  server_error: 'resume',
  unsupported_dataset: 'none',
  permission_missing: 'grant_permission',
  workspace_ambiguous: 'reconnect',
  tenant_mismatch: 'reconnect',
  terms_not_accepted: 'grant_permission',
  disk_full: 'none',
  lease_unavailable: 'wait',
  entitlement_expired: 'resume',
  cancelled: 'none',
  internal: 'contact_admin',
};
